import { useParams } from "#src/Routers/hooks";
import {
  useInvestigation,
  useInvestigationMessages,
  usePostInvestigationMessage,
  useSubmitInvestigationApproval,
} from "#src/components/hooks/adapters/useInvestigations";
import { ConversationFeed } from "#src/routes/investigations/[investigationId]/ConversationFeed";
import { InvestigationComposer } from "#src/routes/investigations/InvestigationComposer";
import { useConversationAutoScroll } from "#src/routes/investigations/[investigationId]/InvestigationDetailPage.helpers";
import {
  type InvestigationDetailPageParameters,
  InvestigationDetailRoutePath,
} from "#src/routes/investigations/[investigationId]/index";
import { InvestigationsRoutePath } from "#src/routes/investigations";
import { AGENT_DISPLAY_NAME } from "#src/routes/investigations/consts";
import { InvestigationErrorBlock } from "#src/routes/investigations/[investigationId]/InvestigationErrorBlock";
import {
  ProgressTicker,
  ProgressTickerVariant,
} from "#src/routes/investigations/[investigationId]/ProgressTicker";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  flattenMessagesOldestFirst,
  isAgentBusy,
  isAgentTurnInProgress,
  isToolInProgress,
  pairPendingApprovals,
} from "#src/view-models/investigations";
import { InvestigationPageLayout } from "#src/routes/investigations/InvestigationPageLayout";
import { generateDocumentTitle, Panel } from "@validereinc/common-components";
import {
  ApprovalDecision,
  InvestigationMessageRole,
  InvestigationStatus,
  MAX_INVESTIGATION_MESSAGE_LENGTH,
  ResourceDefinitions,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import { Titled } from "react-titled";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

export const InvestigationDetailPage = () => {
  const { investigationId } = useParams<InvestigationDetailPageParameters>();
  const [breadcrumbs] = useBreadcrumbsFromRoute(InvestigationDetailRoutePath);
  const { data: investigationData, isLoading: isLoadingInvestigation } =
    useInvestigation(investigationId);

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    isFetchingNextPage: isLoadingOlderMessages,
    hasNextPage: hasOlderMessages,
    fetchNextPage: fetchOlderMessages,
  } = useInvestigationMessages(investigationId);

  // Memoized so message identity is stable across polls when the underlying pages haven't changed.
  const messages = useMemo(
    () => flattenMessagesOldestFirst(messagesData?.pages),
    [messagesData]
  );
  const pendingApprovalPairing = useMemo(
    () => pairPendingApprovals(messages),
    [messages]
  );
  const hasOpenPendingApproval = Boolean(
    pendingApprovalPairing.openPendingApprovalMessage
  );
  const postMessage = usePostInvestigationMessage(investigationId ?? "");
  const submitApproval = useSubmitInvestigationApproval(investigationId ?? "");
  const isConversationSubmitting =
    postMessage.isLoading || submitApproval.isLoading;

  // Scroll-to-bottom should only fire when the newest page changes (new turn arrived) or
  // when a new progress event lands — NOT when the user clicks "load older". Track the
  // newest page's length and the total page count separately so loading older pages
  // (which only grows total page count) doesn't yank the scroll position.
  const newestPage = messagesData?.pages[0];
  const newestMessageCount = newestPage?.data.length ?? 0;
  const progressEventCount = investigationData?.progress.length ?? 0;
  const newestMessageId = newestPage?.data[0]?.id ?? null;
  const newestAgentMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === InvestigationMessageRole.AGENT) {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);

  const {
    scrollAnchorRef,
    conversationScrollRef,
    hasNewMessage,
    jumpToLatest,
  } = useConversationAutoScroll({
    investigationId,
    newestMessageCount,
    progressEventCount,
    newestMessageId,
    newestAgentMessageId,
  });

  const status = investigationData?.status;
  const isArchived = Boolean(investigationData?.archived);
  const isLoadingConversation =
    (isLoadingInvestigation && !investigationData) ||
    (isLoadingMessages && !messagesData);
  const investigationIsBusy = isAgentBusy(status);
  const showAgentProgressTicker = isAgentTurnInProgress(status);
  const showToolInProgressTicker = isToolInProgress(status);
  const showErrorBlock =
    status === InvestigationStatus.ERROR && investigationData?.error;

  const getFollowUpPlaceholder = (): string => {
    if (isArchived) {
      return "Unarchive this investigation to continue...";
    }

    if (isLoadingConversation) {
      return "Waiting for the agent to finish...";
    }

    if (isToolInProgress(status)) {
      return "Waiting for the tool to finish...";
    }

    if (isAgentTurnInProgress(status)) {
      return "Waiting for the agent to finish...";
    }

    if (hasOpenPendingApproval) {
      return "Send a follow-up or approve/reject above...";
    }

    return "Send a follow-up to continue the investigation...";
  };

  return (
    <Titled
      title={generateDocumentTitle(
        ResourceDefinitions.investigation.label.singular,
        InvestigationsRoutePath.title,
        { fallbackExistingTitle: "Validere" }
      )}
    >
      <InvestigationPageLayout
        title={AGENT_DISPLAY_NAME}
        setDocumentTitle={false}
        breadcrumbs={breadcrumbs}
        selectedInvestigationId={investigationId}
      >
        <div className={cx("investigation-detail")}>
          <Panel
            isFluidY
            className={cx("conversation-panel")}
          >
            <div
              ref={conversationScrollRef}
              className={cx("conversation-panel__scroll-area")}
              data-testid="conversation-scroll-area"
            >
              {hasNewMessage ? (
                <button
                  type="button"
                  className={cx("new-message-pill")}
                  onClick={jumpToLatest}
                  data-testid="new-message-pill"
                >
                  New message — jump to latest{" "}
                  <span aria-hidden="true">&darr;</span>
                </button>
              ) : null}
              <div className={cx("conversation-panel__messages")}>
                {isLoadingConversation || investigationData ? (
                  <ConversationFeed
                    isLoading={isLoadingConversation}
                    messages={messages}
                    status={
                      investigationData?.status ?? InvestigationStatus.PENDING
                    }
                    report={investigationData?.report ?? null}
                    pendingApprovalMessageId={
                      hasOpenPendingApproval
                        ? pendingApprovalPairing.openPendingApprovalMessage?.id
                        : null
                    }
                    resolutionsByPendingApprovalId={
                      pendingApprovalPairing.resolutionsByPendingApprovalId
                    }
                    isApprovalLoading={isConversationSubmitting}
                    hasOlderMessages={hasOlderMessages}
                    isLoadingOlderMessages={isLoadingOlderMessages}
                    onApprovalDecision={(decision) => {
                      if (
                        decision !== ApprovalDecision.APPROVE &&
                        decision !== ApprovalDecision.REJECT
                      ) {
                        return;
                      }
                      submitApproval.mutate({ decision });
                    }}
                    onLoadOlder={() => {
                      void fetchOlderMessages();
                    }}
                  />
                ) : null}
              </div>
              <div
                ref={scrollAnchorRef}
                className={cx("scroll-anchor")}
                aria-hidden
              />
            </div>

            {showErrorBlock && investigationData?.error ? (
              <div className={cx("conversation-panel__content-gutter")}>
                <div className={cx("conversation-panel__content-column")}>
                  <InvestigationErrorBlock error={investigationData.error} />
                </div>
              </div>
            ) : null}

            {showAgentProgressTicker && investigationData ? (
              <div className={cx("conversation-panel__content-gutter")}>
                <div className={cx("conversation-panel__content-column")}>
                  <ProgressTicker progress={investigationData.progress} />
                </div>
              </div>
            ) : null}

            {showToolInProgressTicker ? (
              <div className={cx("conversation-panel__content-gutter")}>
                <div className={cx("conversation-panel__content-column")}>
                  <ProgressTicker
                    variant={ProgressTickerVariant.TOOL_IN_PROGRESS}
                  />
                </div>
              </div>
            ) : null}

            {investigationId ? (
              <div className={cx("conversation-panel__content-gutter")}>
                <div className={cx("conversation-panel__content-column")}>
                  <InvestigationComposer
                    textareaLabel="Follow-up"
                    attachmentsInputId="investigation-composer-follow-up-attachments"
                    placeholder={getFollowUpPlaceholder()}
                    maxLengthErrorMessage={`Follow-up must be ${MAX_INVESTIGATION_MESSAGE_LENGTH.toLocaleString()} characters or fewer.`}
                    isDisabled={
                      isLoadingConversation ||
                      isArchived ||
                      investigationIsBusy ||
                      submitApproval.isLoading
                    }
                    isLoading={postMessage.isLoading}
                    onSubmit={async (payload) => {
                      await postMessage.mutateAsync({
                        kind: "message",
                        ...payload,
                      });
                    }}
                  />
                </div>
              </div>
            ) : null}
          </Panel>
        </div>
      </InvestigationPageLayout>
    </Titled>
  );
};
